import { useState, useEffect } from "react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem } from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Phone, PhoneCall, MessageCircle, Copy, Check, Video, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const CALLBACK_KEY = "sojStaffCallbackNumber";

const telDigits = (p) => (p || "").replace(/[^\d+]/g, "");
const intlDigits = (p) => {
  let d = (p || "").replace(/[^\d+]/g, "");
  if (d.startsWith("+")) return d.slice(1);
  if (d.startsWith("00")) return d.slice(2);
  if (d.startsWith("0")) return "44" + d.slice(1); // assume UK numbers
  return d;
};

const APPS = {
  webex: { label: "Webex", href: (t, wa, e164) => `webextel://${e164}` },
  dialer: { label: "phone dialer", href: (t) => `tel:${t}` },
  whatsapp: { label: "WhatsApp", href: (t, wa) => `https://wa.me/${wa}` },
  inapp: { label: "Call through the app", href: () => "#", inApp: true },
};

// Personal per-browser preference. "" = follow the organisation default.
export function useCallApp() {
  const [app, setApp] = useState(() => {
    try { return localStorage.getItem("callApp") || ""; } catch { return ""; }
  });
  useEffect(() => {
    const h = () => { try { setApp(localStorage.getItem("callApp") || ""); } catch { /* noop */ } };
    window.addEventListener("callapp-changed", h);
    return () => window.removeEventListener("callapp-changed", h);
  }, []);
  const update = (v) => {
    try { localStorage.setItem("callApp", v); } catch { /* noop */ }
    window.dispatchEvent(new Event("callapp-changed"));
    setApp(v);
  };
  return [app, update];
}

// Organisation-wide default, fetched once and cached.
let _orgDefaultCache = null;
let _orgDefaultFetched = false;
export function useOrgCallApp() {
  const [org, setOrg] = useState(_orgDefaultCache || "inapp");
  useEffect(() => {
    const h = () => setOrg(_orgDefaultCache || "inapp");
    window.addEventListener("orgcallapp-changed", h);
    if (!_orgDefaultFetched) {
      _orgDefaultFetched = true;
      api.get("/call-app-default")
        .then(({ data }) => { _orgDefaultCache = data.default_call_app || "inapp"; window.dispatchEvent(new Event("orgcallapp-changed")); })
        .catch(() => {});
    } else {
      setOrg(_orgDefaultCache || "inapp");
    }
    return () => window.removeEventListener("orgcallapp-changed", h);
  }, []);
  return org;
}

export function PhoneActions({ phone, name = "", memberId = "", onLogCall, className = "" }) {
  const [copied, setCopied] = useState(false);
  const [callApp, setCallApp] = useCallApp();
  const orgDefault = useOrgCallApp();
  const [callOpen, setCallOpen] = useState(false);
  const [callbackNumber, setCallbackNumber] = useState(() => localStorage.getItem(CALLBACK_KEY) || "");
  const [calling, setCalling] = useState(false);
  if (!phone) return <span className="italic text-xs" style={{ color: "var(--sj-text-3)" }}>No phone</span>;

  const startBridgeCall = async () => {
    const cb = callbackNumber.trim();
    if (!cb) { toast.error("Enter the number we should ring you on"); return; }
    localStorage.setItem(CALLBACK_KEY, cb);
    setCalling(true);
    try {
      await api.post("/voice/click-to-call", { to_phone: phone, staff_phone: cb, to_name: name, member_id: memberId });
      toast.success("Calling you now — pick up and we'll connect you");
      setCallOpen(false);
      if (onLogCall) onLogCall();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start the call");
    } finally { setCalling(false); }
  };

  const rawEffective = callApp || orgDefault || "menu"; // "" personal => follow org
  const effective = (rawEffective === "menu" || APPS[rawEffective]) ? rawEffective : "menu"; // ignore removed/unknown apps (e.g. old "skype")
  const tel = telDigits(phone);
  const wa = intlDigits(phone);
  const e164 = "+" + intlDigits(phone);
  const hrefFor = (key) => APPS[key].href(tel, wa, e164);

  const copy = async () => {
    try { await navigator.clipboard.writeText(phone); }
    catch {
      const ta = document.createElement("textarea");
      ta.value = phone; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      ta.remove();
    }
    setCopied(true); toast.success("Number copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const setDefault = (v) => {
    setCallApp(v);
    toast.success(v === "" ? "Now following the organisation's default" : v === "menu" ? "Tapping a number will now ask each time" : `Tapping a number now uses ${APPS[v].label}`);
  };

  const callItem = (key, Icon, label, primary = false) => (
    <DropdownMenuItem asChild>
      <a data-testid={`phone-action-${key}`} href={hrefFor(key)}
         target={key === "whatsapp" ? "_blank" : undefined} rel={key === "whatsapp" ? "noreferrer" : undefined}
         className={`flex items-center gap-2 cursor-pointer ${primary ? "font-medium" : ""}`}>
        <Icon className="h-4 w-4" style={primary ? { color: "var(--sj-primary)" } : undefined} /> {label}
      </a>
    </DropdownMenuItem>
  );

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      {effective !== "menu" && (
        effective === "inapp" ? (
          <button type="button" data-testid="phone-actions-trigger" onClick={() => setCallOpen(true)}
            className="inline-flex items-center gap-1 hover:underline" style={{ color: "var(--sj-secondary)" }}>
            <PhoneCall className="h-3.5 w-3.5 shrink-0" /> {phone}
          </button>
        ) : (
          <a data-testid="phone-actions-trigger" href={hrefFor(effective)}
             className="inline-flex items-center gap-1 hover:underline" style={{ color: "var(--sj-secondary)" }}>
            <Phone className="h-3.5 w-3.5 shrink-0" /> {phone}
          </a>
        )
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {effective !== "menu" ? (
            <button type="button" data-testid="phone-actions-caret" title="More call options" className="inline-flex items-center rounded hover:bg-black/5 p-0.5" style={{ color: "var(--sj-secondary)" }}>
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button type="button" data-testid="phone-actions-trigger" className="inline-flex items-center gap-1 hover:underline" style={{ color: "var(--sj-secondary)" }}>
              <Phone className="h-3.5 w-3.5 shrink-0" /> {phone} <ChevronDown className="h-3 w-3 opacity-60 shrink-0" />
            </button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-60">
          {callItem("webex", Video, "Call with Webex", true)}
          {callItem("dialer", Phone, "Call (phone dialer)")}
          {callItem("whatsapp", MessageCircle, "WhatsApp")}
          <DropdownMenuItem data-testid="phone-action-bridge" onSelect={(e) => { e.preventDefault(); setCallOpen(true); }} className="flex items-center gap-2 cursor-pointer font-medium" style={{ color: "var(--sj-primary)" }}>
            <PhoneCall className="h-4 w-4" /> Call through the app
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="phone-action-copy" onSelect={(e) => { e.preventDefault(); copy(); }} className="flex items-center gap-2 cursor-pointer">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied!" : "Copy number"}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Default when tapped</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={callApp} onValueChange={setDefault}>
            <DropdownMenuRadioItem value="" data-testid="set-default-org">Use organisation default{orgDefault && orgDefault !== "menu" && APPS[orgDefault] ? ` (${APPS[orgDefault].label})` : orgDefault === "menu" ? " (ask each time)" : ""}</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="menu" data-testid="set-default-menu">Ask each time</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="webex" data-testid="set-default-webex">Always Webex</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="dialer" data-testid="set-default-dialer">Always phone dialer</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="whatsapp" data-testid="set-default-whatsapp">Always WhatsApp</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="inapp" data-testid="set-default-inapp">Always call through the app</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={callOpen} onOpenChange={setCallOpen}>
        <DialogContent className="max-w-md" data-testid="bridge-call-dialog">
          <DialogHeader>
            <DialogTitle className="font-head text-xl flex items-center gap-2"><PhoneCall className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /> Call {name || "member"}</DialogTitle>
            <DialogDescription>We'll ring your phone first, then connect you to {name || "the member"} ({phone}). The call goes out from the church's number.</DialogDescription>
          </DialogHeader>
          <div className="mt-1">
            <label className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>Your phone number (we'll ring this)</label>
            <input data-testid="bridge-callback-input" value={callbackNumber} onChange={(e) => setCallbackNumber(e.target.value)} placeholder="e.g. 07123 456789"
              className="mt-1 w-full rounded-lg border px-3 py-2.5 outline-none focus:border-[#0B5E4E]" style={{ borderColor: "var(--sj-border)" }} />
            <p className="text-xs mt-1.5" style={{ color: "var(--sj-text-3)" }}>Saved for next time on this device.</p>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setCallOpen(false)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
            <button type="button" data-testid="bridge-call-start" disabled={calling} onClick={startBridgeCall} className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{calling ? "Calling…" : "Call now"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </span>
  );
}

export default PhoneActions;
